import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertHistory = ({ filters, className = "" }) => {
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Mock historical alert data
  const historicalAlerts = [
    {
      id: 'ALT-2025-001',
      timestamp: new Date('2025-09-30T23:45:00'),
      severity: 'critical',
      status: 'resolved',
      title: 'Unauthorized Access Attempt',
      device: 'SL-001',
      resolvedBy: 'admin@securelock.com',
      resolutionTime: '15 minutes',
      description: 'Multiple failed login attempts detected'
    },
    {
      id: 'ALT-2025-002',
      timestamp: new Date('2025-09-30T22:30:00'),
      severity: 'high',
      status: 'resolved',
      title: 'Device Offline',
      device: 'SL-003',
      resolvedBy: 'security@securelock.com',
      resolutionTime: '8 minutes',
      description: 'Smart lock lost network connection'
    },
    {
      id: 'ALT-2025-003',
      timestamp: new Date('2025-09-30T21:15:00'),
      severity: 'medium',
      status: 'resolved',
      title: 'Unusual Access Pattern',
      device: 'SL-002',
      resolvedBy: 'admin@securelock.com',
      resolutionTime: '25 minutes',
      description: 'Access granted outside normal hours'
    },
    {
      id: 'ALT-2025-004',
      timestamp: new Date('2025-09-30T20:00:00'),
      severity: 'low',
      status: 'resolved',
      title: 'Battery Low Warning',
      device: 'SL-004',
      resolvedBy: 'maintenance@securelock.com',
      resolutionTime: '5 minutes',
      description: 'Device battery level below 20%'
    },
    {
      id: 'ALT-2025-005',
      timestamp: new Date('2025-09-30T19:30:00'),
      severity: 'critical',
      status: 'resolved',
      title: 'Forced Entry Detected',
      device: 'SL-005',
      resolvedBy: 'security@securelock.com',
      resolutionTime: '3 minutes',
      description: 'Physical tampering with lock mechanism'
    },
    {
      id: 'ALT-2025-006',
      timestamp: new Date('2025-09-30T18:45:00'),
      severity: 'high',
      status: 'resolved',
      title: 'Repeated Failed Attempts',
      device: 'SL-001',
      resolvedBy: 'admin@securelock.com',
      resolutionTime: '12 minutes',
      description: 'Same user ID failed authentication 5 times'
    },
    {
      id: 'ALT-2025-007',
      timestamp: new Date('2025-09-30T17:20:00'),
      severity: 'medium',
      status: 'resolved',
      title: 'Firmware Update Required',
      device: 'SL-002',
      resolvedBy: 'tech@securelock.com',
      resolutionTime: '45 minutes',
      description: 'Critical security patch available'
    },
    {
      id: 'ALT-2025-008',
      timestamp: new Date('2025-09-30T16:10:00'),
      severity: 'low',
      status: 'resolved',
      title: 'Scheduled Maintenance',
      device: 'SL-003',
      resolvedBy: 'maintenance@securelock.com',
      resolutionTime: '2 minutes',
      description: 'Routine system health check completed'
    }
  ];

  const filteredAlerts = useMemo(() => {
    let filtered = [...historicalAlerts];

    // Apply filters
    if (filters?.severity !== 'all') {
      filtered = filtered?.filter(alert => alert?.severity === filters?.severity);
    }
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(alert => alert?.status === filters?.status);
    }
    if (filters?.device !== 'all') {
      filtered = filtered?.filter(alert => alert?.device === filters?.device);
    }
    if (filters?.searchTerm) {
      const searchLower = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(alert => 
        alert?.title?.toLowerCase()?.includes(searchLower) ||
        alert?.description?.toLowerCase()?.includes(searchLower) ||
        alert?.device?.toLowerCase()?.includes(searchLower)
      );
    }

    // Apply date range filter
    const now = new Date();
    if (filters?.dateRange !== 'custom') {
      const ranges = {
        '1h': 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      };
      const range = ranges?.[filters?.dateRange];
      if (range) {
        filtered = filtered?.filter(alert => now - alert?.timestamp <= range);
      }
    }

    // Sort
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'timestamp') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [filters, sortBy, sortOrder]);

  const paginatedAlerts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAlerts?.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAlerts, currentPage]);

  const totalPages = Math.ceil(filteredAlerts?.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'high': return 'text-warning';
      case 'medium': return 'text-accent';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg security-shadow-card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center">
            <Icon name="History" size={16} className="text-success" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Alert History</h2>
            <p className="text-sm text-muted-foreground">
              {filteredAlerts?.length} resolved alert{filteredAlerts?.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          className="text-xs"
        >
          Export CSV
        </Button>
      </div>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('id')}
                  className="flex items-center space-x-1 hover:text-foreground security-transition"
                >
                  <span>Alert ID</span>
                  {sortBy === 'id' && (
                    <Icon name={sortOrder === 'asc' ? "ChevronUp" : "ChevronDown"} size={12} />
                  )}
                </button>
              </th>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('timestamp')}
                  className="flex items-center space-x-1 hover:text-foreground security-transition"
                >
                  <span>Timestamp</span>
                  {sortBy === 'timestamp' && (
                    <Icon name={sortOrder === 'asc' ? "ChevronUp" : "ChevronDown"} size={12} />
                  )}
                </button>
              </th>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('severity')}
                  className="flex items-center space-x-1 hover:text-foreground security-transition"
                >
                  <span>Severity</span>
                  {sortBy === 'severity' && (
                    <Icon name={sortOrder === 'asc' ? "ChevronUp" : "ChevronDown"} size={12} />
                  )}
                </button>
              </th>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</th>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Device</th>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Resolved By</th>
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Resolution Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedAlerts?.map((alert) => (
              <tr key={alert?.id} className="hover:bg-muted/30 security-transition">
                <td className="p-3 text-sm font-mono text-foreground">{alert?.id}</td>
                <td className="p-3 text-sm text-muted-foreground">{formatTimestamp(alert?.timestamp)}</td>
                <td className="p-3">
                  <span className={`text-sm font-medium capitalize ${getSeverityColor(alert?.severity)}`}>
                    {alert?.severity}
                  </span>
                </td>
                <td className="p-3 text-sm text-foreground">{alert?.title}</td>
                <td className="p-3 text-sm text-muted-foreground">{alert?.device}</td>
                <td className="p-3 text-sm text-muted-foreground">{alert?.resolvedBy}</td>
                <td className="p-3 text-sm text-success">{alert?.resolutionTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-border">
        {paginatedAlerts?.map((alert) => (
          <div key={alert?.id} className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground">{alert?.title}</h3>
                <p className="text-xs text-muted-foreground font-mono">{alert?.id}</p>
              </div>
              <span className={`text-xs font-medium capitalize px-2 py-1 rounded ${getSeverityColor(alert?.severity)}`}>
                {alert?.severity}
              </span>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={12} />
                <span>{formatTimestamp(alert?.timestamp)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={12} />
                <span>{alert?.device}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="User" size={12} />
                <span>Resolved by {alert?.resolvedBy}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Timer" size={12} />
                <span className="text-success">Resolved in {alert?.resolutionTime}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAlerts?.length)} of {filteredAlerts?.length} results
          </p>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
            />
            <span className="text-sm text-foreground px-2">
              {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
            />
          </div>
        </div>
      )}
      {filteredAlerts?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No alerts found</h3>
          <p className="text-muted-foreground">Try adjusting your filters to see more results.</p>
        </div>
      )}
    </div>
  );
};

export default AlertHistory;